// Verbindungs-Assistent — reine Funktionen, engine-frei.
// Zwei aneinanderstoßende Teile bekommen symmetrische Verbinder-Bohrungen auf
// beiden Teilen (meta.cutouts, source "joinery"). v1 arbeitet auf Welt-AABBs
// und deckt damit achsparallel gedrehte Teile ab (Rotationen in 90°-Schritten);
// alles andere liefert null. Schräg-/Gehrungsstöße sind damit nicht „kein
// Kontakt“, sondern schlicht noch nicht unterstützt — jointIssue/jointIssueMessage
// unterscheiden beides, damit die UI ehrlich bleibt.
//
// Getroffene Fläche entscheidet über die Bohrart: trifft der Stoß die Teil-
// Fläche, entsteht eine Flächenbohrung (axis "y", side top/bottom); trifft er
// eine Schmalseite, eine echte Stirnbohrung (axis "x"/"z", vgl. cutouts.js).
//
// Exzenter, beidseitiger Flächenkontakt (Stapel, zwei Platten übereinander):
// „Fläche schlägt Stirnseite“ entscheidet hier nichts — beide Teile werden auf
// der Fläche getroffen. Regel: **das dickere Teil bekommt den Topf**, bei
// gleicher Stärke das zuerst selektierte (Teil A). Begründung: der Topf ist
// Ø15 × 12,5 mm tief; im dickeren Teil bleibt am meisten Restholz unter dem
// Topfboden, das Durchdrücken der Fläche ist am unwahrscheinlichsten. Die Regel
// ist unabhängig von der Auswahlreihenfolge, solange die Stärken verschieden
// sind — zweimal derselbe Stoß ergibt zweimal dasselbe Bohrbild.
//
// Kollisionen: geplante Bohrpunkte werden gegen vorhandene Ausschnitte,
// Bohrungen und Nuten/Fälze beider Teile geprüft (obstacles.js). Belegte Punkte
// weichen im 5-mm-Raster entlang der Stoßkante aus, innerhalb der zulässigen
// Randabstände; findet sich kein freier Platz, fällt der Punkt weg. jointCutouts
// meldet beides über `moved`/`skipped` zurück.
import { partAabb, rotateLocalVector } from "./snapping"
import { cutoutClashes, WEB_MM } from "./obstacles"
import { cutoutsOf } from "./cutouts"

const UNIT = [ [ 1, 0, 0 ], [ 0, 1, 0 ], [ 0, 0, 1 ] ]

// Bohrbilder der Verbinder (vgl. HARDWARE_PRESETS „Exzenter 15 mm“/„Dübel 8×30“)
export const JOINT_TYPES = {
  duebel: { label: "Dübel" },      // beidseitig Ø8, Tiefe 15
  exzenter: { label: "Exzenter" }  // Topf Ø15 T12,5 (≥ 34 mm von der Kante) + Ø8 T10 im Stirnholz
}

const DUEBEL_BORE = { diameter: 8, depth: 15 }
const TOPF_BORE = { diameter: 15, depth: 12.5, fromOppositeSide: true }
const BOLT_BORE = { diameter: 8, depth: 10 }

// Ausweich-Raster entlang der Stoßkante: 5 mm ist fein genug, um an einem
// Ausschnitt vorbeizukommen, und grob genug für runde Werkstattmaße.
const EVASION_GRID_MM = 5

// Gründe, warum der Assistent nicht arbeiten kann — jointContact liefert für
// beide Fälle null, die Meldung muss aber unterscheiden.
export const JOINT_MESSAGES = {
  skewed: "Schräge Stöße unterstützt der Assistent noch nicht — Bohrungen von Hand setzen",
  "no-contact": "Teile berühren sich nicht"
}

const isRightAngle = (deg) => Math.abs(deg / 90 - Math.round(deg / 90)) < 1e-9
const axisAligned = (part) => isRightAngle(part.rot_x) && isRightAngle(part.rot_y) && isRightAngle(part.rot_z)

// R^T · v — Weltvektor in Teil-lokale Richtung zurückdrehen
// (rotateLocalVector liefert die Spalten von R, die Transponierte invertiert).
function worldToLocalVector(part, vector) {
  return UNIT.map((axis) => {
    const column = rotateLocalVector(part, axis)
    return column[0] * vector[0] + column[1] * vector[1] + column[2] * vector[2]
  })
}

function worldToLocalPoint(part, point) {
  return worldToLocalVector(part, [ point[0] - part.pos_x, point[1] - part.pos_y, point[2] - part.pos_z ])
}

// Stoßfläche zweier achsparalleler Teile: auf einer Achse berühren sich zwei
// Flächen innerhalb der Toleranz, auf den beiden Querachsen überlappen die
// AABBs. Rückgabe null, wenn kein Kontakt (oder Teile schief gedreht sind).
export function jointContact(partA, partB, toleranceMm = 2) {
  if (!axisAligned(partA) || !axisAligned(partB)) return null
  const a = partAabb(partA)
  const b = partAabb(partB)

  let best = null
  for (const axis of [ 0, 1, 2 ]) {
    const candidates = [
      // A.max berührt B.min → Kontaktnormale von A zeigt in +axis
      { aSide: "max", gap: Math.abs(b.min[axis] - a.max[axis]), plane: (a.max[axis] + b.min[axis]) / 2 },
      { aSide: "min", gap: Math.abs(a.min[axis] - b.max[axis]), plane: (a.min[axis] + b.max[axis]) / 2 }
    ]
    for (const candidate of candidates) {
      if (candidate.gap > toleranceMm) continue
      const cross = [ 0, 1, 2 ].filter((i) => i !== axis)
      const ranges = cross.map((i) => [ Math.max(a.min[i], b.min[i]), Math.min(a.max[i], b.max[i]) ])
      if (ranges.some(([ lo, hi ]) => hi - lo <= 0)) continue
      if (best && candidate.gap >= best.gap) continue

      // Bohrpunkte laufen entlang der längeren Querachse (spanAxis)
      const spanIndex = ranges[0][1] - ranges[0][0] >= ranges[1][1] - ranges[1][0] ? 0 : 1
      best = {
        axis,
        plane: candidate.plane,
        aSide: candidate.aSide,
        gap: candidate.gap,
        spanAxis: cross[spanIndex],
        overlapRange: ranges[spanIndex],
        crossAxis: cross[1 - spanIndex],
        crossRange: ranges[1 - spanIndex]
      }
    }
  }
  return best
}

// Warum der Assistent hier nicht arbeiten kann — Schlüssel für JOINT_MESSAGES,
// null bei einem brauchbaren Stoß. Trennt den ehrlichen „noch nicht unterstützt“-
// Fall (Schräg-/Gehrungsstoß) vom schlichten „berührt sich nicht“.
export function jointIssue(partA, partB, toleranceMm = 2) {
  if (!axisAligned(partA) || !axisAligned(partB)) return "skewed"
  if (!jointContact(partA, partB, toleranceMm)) return "no-contact"
  return null
}

// Fertige Meldung, wenn der Assistent nicht arbeiten kann. Fällt auf
// „berühren sich nicht“ zurück, falls der Stoß selbst in Ordnung ist und es an
// etwas anderem lag (unbekannter Verbindertyp o. ä.).
export function jointIssueMessage(partA, partB) {
  return JOINT_MESSAGES[jointIssue(partA, partB)] ?? JOINT_MESSAGES["no-contact"]
}

// Zulässiges Fenster für Bohrpunkte auf der Stoßkante (Randabstand beidseits;
// kurze Stöße ziehen ihn auf ein Viertel der Länge zusammen). Gleiche Grenzen
// für die Erstverteilung und für das Ausweichen bei Kollisionen.
export function jointWindow(contact, { edgeOffsetMm = 50 } = {}) {
  const [ start, end ] = contact.overlapRange
  const offset = Math.min(edgeOffsetMm, (end - start) / 4)
  return [ start + offset, end - offset ]
}

// Bohrpunkte (Weltkoordinaten) entlang der Überlappung: Randabstand beidseits,
// dazwischen gleichmäßig (System-32-Raster: max. 224 mm Abstand), mindestens 2.
export function jointPositions(contact, { edgeOffsetMm = 50, maxSpacingMm = 224 } = {}) {
  const [ first, last ] = jointWindow(contact, { edgeOffsetMm })
  const count = Math.max(2, Math.ceil((last - first) / maxSpacingMm) + 1)
  const step = (last - first) / (count - 1)
  const crossCenter = (contact.crossRange[0] + contact.crossRange[1]) / 2

  return Array.from({ length: count }, (_, i) => {
    const point = [ 0, 0, 0 ]
    point[contact.axis] = contact.plane
    point[contact.spanAxis] = Math.round(first + step * i)
    point[contact.crossAxis] = crossCenter
    return point
  })
}

// Berührte Fläche eines Teils: Kontaktnormale (Weltrichtung zum anderen Teil)
// zurückrotiert — lokale Achse 1 (Stärke) = Fläche, sonst Stirnseite.
function contactFace(part, axis, outwardSign) {
  const world = [ 0, 0, 0 ]
  world[axis] = outwardSign
  const local = worldToLocalVector(part, world)
  const localAxis = [ 0, 1, 2 ].reduce((m, i) => (Math.abs(local[i]) > Math.abs(local[m]) ? i : m), 0)
  return { localAxis, isFace: localAxis === 1, sign: Math.sign(local[localAxis]) }
}

function bore(part, face, point, { diameter, depth, fromOppositeSide = false, jointId = null }) {
  const local = worldToLocalPoint(part, point)
  const contactSide = face.sign < 0 ? "bottom" : "top"

  // Schmalseite getroffen → Stirnbohrung entlang der lokalen Länge/Breite;
  // die Lage auf der Bohrachse steckt in side, quer in y_mm + der Restachse.
  if (!face.isFace) {
    const axis = face.localAxis === 0 ? "x" : "z"
    return {
      shape: "circle",
      axis,
      diameter_mm: diameter,
      depth_mm: depth,
      side: contactSide,
      [axis === "x" ? "z_mm" : "x_mm"]: Math.round(local[axis === "x" ? 2 : 0]),
      y_mm: Math.round(local[1]),
      source: "joinery",
      ...(jointId ? { joint_id: jointId } : {})
    }
  }

  const side = fromOppositeSide ? (contactSide === "top" ? "bottom" : "top") : contactSide
  return {
    shape: "circle",
    diameter_mm: diameter,
    depth_mm: depth,
    side,
    x_mm: Math.round(local[0]),
    z_mm: Math.round(local[2]),
    source: "joinery",
    ...(jointId ? { joint_id: jointId } : {})
  }
}

// Stabiler Schlüssel eines Stoßes — richtungsunabhängig, damit erneutes
// Verbinden derselben zwei Teile die vorhandene Verbindung wiedererkennt
// und erneuert, statt eine zweite daneben zu setzen.
export function jointIdFor(partA, partB) {
  return [ partA.client_id, partB.client_id ].sort().join("~")
}

// Bohrungen eines Teils ohne die dieses Stoßes (Grundlage fürs Erneuern:
// erst abräumen, dann neu verteilen — sonst blockieren die alten Bohrungen
// als vermeintliche Hindernisse die neuen Punkte).
export function cutoutsWithoutJoint(part, jointId) {
  return cutoutsOf(part).filter((cutout) => cutout.joint_id !== jointId)
}

// Topf muss ≥ 34 mm von der Plattenkante sitzen: Bohrpunkt auf der Querachse
// von der nächstliegenden Kante des Topf-Teils nach innen schieben.
function topfPoint(part, contact, point) {
  const aabb = partAabb(part)
  const lo = aabb.min[contact.crossAxis]
  const hi = aabb.max[contact.crossAxis]
  const c = point[contact.crossAxis]
  if (Math.min(c - lo, hi - c) >= 34) return point
  const shifted = [ ...point ]
  shifted[contact.crossAxis] = c - lo <= hi - c
    ? Math.min(lo + 34, (lo + hi) / 2)
    : Math.max(hi - 34, (lo + hi) / 2)
  return shifted
}

// Wer bekommt den Exzenter-Topf? Normalfall: das flächig getroffene Teil (der
// Bolzen gehört ins Stirnholz). Bei beidseitigem Flächenkontakt (Stapel) das
// dickere Teil, bei Gleichstand Teil A — siehe Modulkopf.
function topfOnA(partA, partB, faceA, faceB) {
  if (faceA.isFace && faceB.isFace) return partA.thickness_mm >= partB.thickness_mm
  return faceA.isFace || !faceB.isFace
}

// Bohrungs-Paar für einen Welt-Bohrpunkt: { a, b } in teil-lokalen Koordinaten.
function boresAt(type, joint, point) {
  const { partA, partB, faceA, faceB, contact, topfIsA, jointId } = joint
  const tag = { jointId }
  if (type === "duebel") {
    // beidseitig Ø8, Tiefe 15 (vgl. Dübel 8×30: je 15 mm pro Teil)
    return {
      a: bore(partA, faceA, point, { ...DUEBEL_BORE, ...tag }),
      b: bore(partB, faceB, point, { ...DUEBEL_BORE, ...tag })
    }
  }
  // Exzenter: Topf (öffnet zur freien Seite) + Bolzen im Gegenstück
  const [ topfPart, topfFace ] = topfIsA ? [ partA, faceA ] : [ partB, faceB ]
  const [ boltPart, boltFace ] = topfIsA ? [ partB, faceB ] : [ partA, faceA ]
  const topf = bore(topfPart, topfFace, topfPoint(topfPart, contact, point), { ...TOPF_BORE, ...tag })
  const bolt = bore(boltPart, boltFace, point, { ...BOLT_BORE, ...tag })
  return topfIsA ? { a: topf, b: bolt } : { a: bolt, b: topf }
}

// Freien Platz für einen Bohrpunkt suchen: erst der Sollpunkt, dann im Raster
// abwechselnd nach beiden Seiten — so bleibt die Verteilung so gleichmäßig wie
// möglich. Außerhalb des Randabstand-Fensters wird nicht gesucht.
function evade(point, spanAxis, [ lo, hi ], probe, gridMm) {
  const onSpot = probe(point)
  if (onSpot) return { bores: onSpot, moved: false }

  for (let step = gridMm; step <= hi - lo; step += gridMm) {
    for (const direction of [ 1, -1 ]) {
      const value = point[spanAxis] + direction * step
      if (value < lo || value > hi) continue
      const candidate = [ ...point ]
      candidate[spanAxis] = value
      const bores = probe(candidate)
      if (bores) return { bores, moved: true }
    }
  }
  return null
}

// Liefert je Teil die neuen meta.cutouts-Einträge (teil-lokale Koordinaten)
// für die übergebenen Welt-Bohrpunkte — oder null ohne Kontakt.
// Rückgabe { a, b, moved, skipped }: moved = wegen Kollision versetzte Punkte,
// skipped = mangels freiem Platz weggelassene. a/b können leer sein.
export function jointCutouts(partA, partB, type, positions, options = {}) {
  if (!JOINT_TYPES[type] || !positions?.length) return null
  const contact = jointContact(partA, partB)
  if (!contact) return null

  const { clearanceMm = WEB_MM, gridMm = EVASION_GRID_MM } = options
  const signA = contact.aSide === "max" ? 1 : -1
  const faceA = contactFace(partA, contact.axis, signA)
  const faceB = contactFace(partB, contact.axis, -signA)
  const jointId = jointIdFor(partA, partB)
  const joint = { partA, partB, faceA, faceB, contact, jointId, topfIsA: topfOnA(partA, partB, faceA, faceB) }

  const bounds = jointWindow(contact, options)
  const a = []
  const b = []
  let moved = 0
  let skipped = 0

  // frei = weder mit dem Bestand noch mit den bereits gesetzten Bohrungen
  // dieses Laufs in Konflikt (sonst rückt ein ausweichender Punkt dem Nachbarn
  // auf die Pelle)
  const probe = (candidate) => {
    const bores = boresAt(type, joint, candidate)
    if (cutoutClashes(partA, bores.a, { clearanceMm, extra: a })) return null
    if (cutoutClashes(partB, bores.b, { clearanceMm, extra: b })) return null
    return bores
  }

  for (const point of positions) {
    const placed = evade(point, contact.spanAxis, bounds, probe, gridMm)
    if (!placed) {
      skipped += 1
      continue
    }
    if (placed.moved) moved += 1
    a.push(placed.bores.a)
    b.push(placed.bores.b)
  }

  return { a, b, moved, skipped }
}

// Statusmeldung für den Verbindungs-Assistenten.
export function joinSummary({ a, moved, skipped, replaced = false }) {
  if (a.length === 0) return "Kein Platz für Verbinder (Kollision)"
  const parts = [ `${a.length} Verbinder ${replaced ? "erneuert" : "gesetzt"}` ]
  if (moved > 0) parts.push(`${moved} versetzt`)
  if (skipped > 0) parts.push(`${skipped} ausgelassen (Kollision)`)
  return parts.join(", ")
}
